import { ColorRgbModel } from './color-rgb.model';
import { ColorHsbModel } from './color-hsb.model';
/* eslint-disable @typescript-eslint/naming-convention */
// @dynamic
export class ColorModel {
    hex: string;
    rgb: ColorRgbModel;
    hsb: ColorHsbModel;
    relativeLuminance: number;

    constructor(
        hex: string,
        rgb: ColorRgbModel,
        hsb: ColorHsbModel
    ) {
        this.hex = hex;
        this.rgb = rgb;
        this.hsb = hsb;
    }

    static isValidHex(color: string): boolean {
        return /^#[0-9A-F]{6}$/i.test(color);
    }

    static isValidRgb(colorValue: number): boolean {
        return colorValue <= 255 && colorValue >= 0 && !isNaN(colorValue);
    }

    static validateHSB(hsb: ColorHsbModel): ColorHsbModel {
        return {
            h: Math.min(360, Math.max(0, hsb.h)),
            s: Math.min(100, Math.max(0, hsb.s)),
            b: Math.min(100, Math.max(0, hsb.b))
        };
    }

    static createFromHex(hex: string): ColorModel {
        if (!ColorModel.isValidHex(hex)) {
            hex = '#000000';
        }

        const rgb: ColorRgbModel = ColorModel.HEXtoRGB(hex);
        const hsb: ColorHsbModel = ColorModel.HEXtoHSB(hex);

        return new ColorModel(hex, rgb, hsb);
    }

    static HEXtoRGB(hex: string): ColorRgbModel {
        if (!hex) {
            return null;
        }
        const hexRGB = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

        const rgb: ColorRgbModel = {
            r: parseInt(hexRGB[1], 16),
            g: parseInt(hexRGB[2], 16),
            b: parseInt(hexRGB[3], 16)
        };

        return rgb;
    }

    static HEXtoHSB(hex: string): ColorHsbModel {
        return ColorModel.RGBtoHSB(this.HEXtoRGB(hex));
    }

    static RGBtoHSB(rgb: ColorRgbModel): ColorHsbModel {
        const hsb = {
            h: 0,
            s: 0,
            b: 0
        };
        const min = Math.min(rgb.r, rgb.g, rgb.b);
        const max = Math.max(rgb.r, rgb.g, rgb.b);
        const delta = max - min;
        hsb.b = max;
        hsb.s = max !== 0 ? 255 * delta / max : 0;
        if (hsb.s !== 0) {
            if (rgb.r === max) {
                hsb.h = (rgb.g - rgb.b) / delta;
            } else if (rgb.g === max) {
                hsb.h = 2 + (rgb.b - rgb.r) / delta;
            } else {
                hsb.h = 4 + (rgb.r - rgb.g) / delta;
            }
        } else {
            hsb.h = -1;
        }
        hsb.h *= 60;
        if (hsb.h < 0) {
            hsb.h += 360;
        }
        hsb.s *= 100 / 255;
        hsb.b *= 100 / 255;

        return hsb;
    }

    static RGBtoHEX(rgb: ColorRgbModel): string {
        const hex = [
            ColorModel.fixHex((+rgb.r).toString(16).toUpperCase()),
            ColorModel.fixHex((+rgb.g).toString(16).toUpperCase()),
            ColorModel.fixHex((+rgb.b).toString(16).toUpperCase())
        ];

        return hex.join('');
    }

    static fixHex(hex: string): string {
        if (hex.length === 0) {
            hex = '00';
        }
        if (hex.length === 1) {
            hex = '0' + hex;
        }

        return hex;
    }

    static HSBtoRGB(hsb: ColorHsbModel): ColorRgbModel {
        let rgb = {
            r: null, g: null, b: null
        };
        let h = Math.round(hsb.h);
        const s = Math.round(hsb.s * 255 / 100);
        const v = Math.round(hsb.b * 255 / 100);
        if (s === 0) {
            rgb = {
                r: v,
                g: v,
                b: v
            };
        } else {
            const t1 = v;
            const t2 = (255 - s) * v / 255;
            const t3 = (t1 - t2) * (h % 60) / 60;
            if (h === 360) {
                h = 0;
            }
            if (h < 60) {
                rgb.r = t1;
                rgb.b = t2;
                rgb.g = t2 + t3;
            } else if (h < 120) {
                rgb.g = t1;
                rgb.b = t2;
                rgb.r = t1 - t3;
            } else if (h < 180) {
                rgb.g = t1;
                rgb.r = t2;
                rgb.b = t2 + t3;
            } else if (h < 240) {
                rgb.b = t1;
                rgb.r = t2;
                rgb.g = t1 - t3;
            } else if (h < 300) {
                rgb.b = t1;
                rgb.g = t2;
                rgb.r = t2 + t3;
            } else if (h < 360) {
                rgb.r = t1;
                rgb.g = t2;
                rgb.b = t1 - t3;
            } else {
                rgb.r = 0;
                rgb.g = 0;
                rgb.b = 0;
            }
        }

        return { r: Math.round(rgb.r), g: Math.round(rgb.g), b: Math.round(rgb.b) };
    }

    static HSBtoHEX(hsb: ColorHsbModel): string {
        return this.RGBtoHEX(this.HSBtoRGB(hsb));
    }

    clone(): ColorModel {
        return new ColorModel(
            this.hex,
            this.rgb,
            this.hsb
        );
    }

    isValid(): boolean {
        // Test hex for validity.
        if (!ColorModel.isValidHex(this.hex)) {
            return false;
        }

        // Test rgb for validity.
        if (!ColorModel.isValidRgb(this.rgb.r) || !ColorModel.isValidRgb(this.rgb.g) || !ColorModel.isValidRgb(this.rgb.b)) {
            return false;
        }

        return true;
    }

    getRelativeLuminance(): number {
        if (this.relativeLuminance || this.relativeLuminance === 0) {
            return this.relativeLuminance;
        }
        this.rgb = this.rgb || ColorModel.HEXtoRGB(this.hex);
        if (this.rgb) {
            const r = this.calculateSRGB(this.rgb.r / 255);
            const g = this.calculateSRGB(this.rgb.g / 255);
            const b = this.calculateSRGB(this.rgb.b / 255);
            this.relativeLuminance = r * 0.2126 + g * 0.7152 + b * 0.0722;
            return this.relativeLuminance;
        } else {
            return 0;
        }
    }

    private calculateSRGB(color: number): number {
        return color <= 0.03928 ?
            color / 12.92
            : Math.pow((color + 0.055) / 1.055, 2.4);
    }

    applyOpacity(opacity: number): ColorModel {
        this.rgb = this.rgb || ColorModel.HEXtoRGB(this.hex);
        if (this.rgb) {
            this.rgb.r = Math.floor(255 - opacity * (255 - this.rgb.r));
            this.rgb.g = Math.floor(255 - opacity * (255 - this.rgb.g));
            this.rgb.b = Math.floor(255 - opacity * (255 - this.rgb.b));
        }

        return this;
    }
}
